import React, { useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import DropdownNewGap from '../DropdownNewGap';
import DropdownClassic from '../DropdownClassic';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  'image/webp',
  'image/bmp',
  'image/tiff',
];

function CreateGapAnalysisModal({ open, onClose, onCreated, userRole, customers, frameworks }) {
  const [title, setTitle] = useState('');
  const [customer, setCustomer] = useState(null);
  const [framework, setFramework] = useState('');
  const [start, setStart] = useState('');
  const [finish, setFinish] = useState('');
  const [comments, setComments] = useState('');
  const [permissionLevel, setPermissionLevel] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-select org for regular users
  React.useEffect(() => {
    if (userRole === 'user' && customers?.length > 0 && customer === null) {
      setCustomer(customers[0].id);
    }
  }, [userRole, customers, customer]);

  const resetForm = () => {
    setTitle('');
    setCustomer(null);
    setFramework('');
    setStart('');
    setFinish('');
    setComments('');
    setPermissionLevel(0);
    setUploadedFiles([]);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose && onClose();
  };

  const handleFileUpload = (e) => {
    const fileList = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (fileList && fileList.length > 0) {
      const valid = Array.from(fileList).filter((f) => ALLOWED_FILE_TYPES.includes(f.type));
      if (valid.length > 0) setUploadedFiles([...uploadedFiles, ...valid]);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Reference is required'); return; }
    if (customer === null && customer === undefined) { setError('Organization is required'); return; }

    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      const engagementData = {
        subtype_id: 7, // Gap Analysis
        // The dropdown now carries the real id, so nothing is reconstructed
        // here. Adding one to an index worked until an id stopped being its
        // own position.
        framework_id: framework !== '' ? framework : null,
        title: title.trim(),
        customer_id: customer,
        start,
        finish,
        comments: comments.trim(),
        status: 'not started',
        permission_level: permissionLevel,
      };

      formData.append('engagementData', JSON.stringify(engagementData));

      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => formData.append('files', file));
      }

      const res = await axios.post(`${Config.APIBaseURL}/engagement/add/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          access_token: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (res.status === 200 || res.status === 201) {
        resetForm();
        onCreated && onCreated(res.data);
        onClose && onClose();
      }
    } catch (err) {
      console.error('Error creating gap analysis:', err);
      setError(err.response?.data?.detail || 'Failed to create engagement');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-[900px] max-h-[92vh] overflow-auto rounded-2xl bg-slate-900/95 shadow-2xl border border-slate-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-semibold text-white">New Gap Analysis</h3>
            <p className="text-xs text-slate-500 mt-1">Create a new compliance assessment engagement</p>
          </div>
          <button
            className="text-sm px-3 py-1.5 rounded-md bg-slate-800 text-slate-300 hover:bg-slate-700"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-sm text-rose-300">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            {/* Reference */}
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">Reference</label>
              <input
                className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. ISO 27001 Assessment Q2 2026"
              />
            </div>

            {/* Organization */}
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">Organization</label>
              {userRole === 'user' ? (
                <div className="px-3 py-2 bg-slate-950/70 border border-slate-800 rounded-md text-slate-400 text-sm">
                  {customers?.length > 0
                    ? customers[0]?.label
                    : JSON.parse(localStorage.getItem('userinfo') || '{}')?.org_name || 'Your organization'}
                </div>
              ) : customers?.length > 0 ? (
                <DropdownNewGap
                  options={customers}
                  style="w-full"
                  onChange={setCustomer}
                  value={customer}
                />
              ) : (
                <div className="animate-pulse bg-slate-800 h-9 rounded-md" />
              )}
            </div>

            {/* Framework — full width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 text-slate-300">Framework / Standard</label>
              {frameworks?.length > 0 ? (
                <DropdownClassic
                  options={frameworks}
                  style="w-full"
                  onChange={setFramework}
                />
              ) : (
                <div className="animate-pulse bg-slate-800 h-9 rounded-md" />
              )}
            </div>

            {/* Dates */}
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">Start Date</label>
              <input
                className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">End Date</label>
              <input
                className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                type="date"
                value={finish}
                onChange={(e) => setFinish(e.target.value)}
              />
            </div>

            {/* Files */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 text-slate-300">Files</label>
              <div
                className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-indigo-500/60 transition cursor-pointer"
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFileUpload(e); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={() => document.getElementById('gap-file-upload').click()}
              >
                <svg className="w-8 h-8 mx-auto text-slate-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-slate-400">Drag & drop files, or click to select</p>
              </div>
              <input
                id="gap-file-upload"
                className="hidden"
                type="file"
                accept={ALLOWED_FILE_TYPES.join(',')}
                onChange={handleFileUpload}
                multiple
              />
              {uploadedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {uploadedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-900/50 rounded-md border border-slate-800">
                      <span className="text-xs text-slate-300 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setUploadedFiles(uploadedFiles.filter((_, j) => j !== i)); }}
                        className="text-xs text-slate-500 hover:text-rose-400"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 text-slate-300">Notes</label>
              <textarea
                rows={3}
                className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-3 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Additional context or instructions..."
              />
            </div>

            {/* Permissions */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 text-slate-300">Visibility</label>
              <div className="flex gap-2">
                {[
                  { id: 0, label: 'Only me' },
                  { id: 1, label: 'Org read' },
                  { id: 2, label: 'Org read + write' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPermissionLevel(opt.id)}
                    className={`px-3 py-2 rounded-md text-sm transition ${
                      permissionLevel === opt.id
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-900/60 text-slate-200 border border-slate-800 hover:bg-slate-800/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
          <button
            className="text-sm px-5 py-2 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            disabled={submitting}
            className="text-sm px-5 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSubmit}
          >
            {submitting ? 'Creating...' : 'Create Assessment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateGapAnalysisModal;
